import { Select, type SelectProps } from "@mantine/core"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

export function TimezoneInput({ value, onChange, ...props }: Partial<SelectProps>) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language == "en" ? "en-GB" : i18n.language

  const data = useMemo(() => {
    let timezones = Intl.supportedValuesOf("timeZone")
    if (!timezones.includes("UTC")) timezones = ["UTC", ...timezones]

    const grouped = new Map<string, { group: string; items: { value: string; label: string }[] }>()

    for (const timezone of timezones) {
      const formatter = new Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName: "longGeneric" })
      const timeZoneName = formatter.formatToParts(new Date()).find((part) => part.type == "timeZoneName")?.value
      const group = timeZoneName || "Other"
      const section = grouped.get(group) || { group, items: [] }
      section.items.push({ value: timezone, label: timezone })
      grouped.set(group, section)
    }

    return [...grouped.values()]
      .map((section) => ({
        ...section,
        items: section.items.toSorted((a, b) => a.label.localeCompare(b.label)),
      }))
      .toSorted((a, b) => a.group.localeCompare(b.group, locale))
  }, [locale])

  return (
    <Select
      searchable
      allowDeselect={false}
      data={data}
      value={value}
      onChange={onChange}
      filter={({ options, search }) => {
        const query = search.trim().toLowerCase()
        if (!query) return options

        return options.flatMap((option: any) => {
          if (option.group && Array.isArray(option.items)) {
            const groupMatch = option.group.toLowerCase().includes(query)
            const items = groupMatch
              ? option.items
              : option.items.filter((item: any) => {
                  const label = item.label?.toLowerCase() || ""
                  const code = item.value?.toLowerCase() || ""
                  return label.includes(query) || code.includes(query)
                })
            return items.length > 0 ? [{ ...option, items }] : []
          }

          const label = option.label?.toLowerCase() || ""
          const code = option.value?.toLowerCase() || ""
          return label.includes(query) || code.includes(query) ? [option] : []
        })
      }}
      label={t("Pick timezone")}
      placeholder={t("Select timezone")}
      {...props}
    />
  )
}
